/**
 * Production layout primitives — Section, Container, Stack, Inline, Split, Grid, Divider,
 * Surface. Structure + shared styling only (no section-specific layout). Section is the
 * single place that applies the RuntimeTheme → CSS variables (reusing the preserved
 * `themeVars` interpreter) and the RTL direction.
 */
import type { CSSProperties, ElementType, ReactNode } from 'react'
import type { RuntimeTheme } from '../../runtime/types'
import type { WebsiteLocale } from '../../schema'
import type { Align, Tone } from './types'
import { ALIGN_CLASS } from './types'
import { cx } from './utils'

interface SectionProps {
  readonly theme: RuntimeTheme
  readonly locale?: WebsiteLocale
  readonly tone?: Tone
  readonly id?: string
  readonly as?: 'section' | 'header' | 'footer' | 'div'
  readonly flush?: boolean
  readonly ariaLabel?: string
  readonly ariaLabelledBy?: string
  readonly className?: string
  readonly children: ReactNode
}

export function Section({
  theme,
  locale,
  tone = 'default',
  id,
  as = 'section',
  flush,
  ariaLabel,
  ariaLabelledBy,
  className,
  children,
}: SectionProps) {
  const Tag = as as ElementType
  return (
    <Tag
      id={id}
      data-ds={theme.designSystem}
      dir={locale === 'ar' ? 'rtl' : undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={cx(
        'sbp-section',
        tone === 'alt' && 'sbp-section--alt',
        tone === 'dark' && 'sbp-section--dark',
        flush && 'sbp-section--flush',
        className,
      )}
    >
      {children}
    </Tag>
  )
}

interface ContainerProps {
  readonly size?: 'default' | 'narrow' | 'wide'
  readonly className?: string
  readonly children: ReactNode
}

export function Container({ size = 'default', className, children }: ContainerProps) {
  return (
    <div
      className={cx(
        'sbp-container',
        size === 'narrow' && 'sbp-container--narrow',
        size === 'wide' && 'sbp-container--wide',
        className,
      )}
    >
      {children}
    </div>
  )
}

interface StackProps {
  readonly gap?: string
  readonly align?: Align
  readonly as?: 'div' | 'ul' | 'ol'
  readonly className?: string
  readonly children: ReactNode
}

export function Stack({ gap, align, as = 'div', className, children }: StackProps) {
  const Tag = as as ElementType
  const style = gap ? ({ '--sbp-stack-gap': gap } as CSSProperties) : undefined
  return (
    <Tag style={style} className={cx('sbp-stack', align && ALIGN_CLASS[align], className)}>
      {children}
    </Tag>
  )
}

interface InlineProps {
  readonly gap?: string
  readonly justify?: Align
  readonly nowrap?: boolean
  readonly className?: string
  readonly children: ReactNode
}

export function Inline({ gap, justify, nowrap, className, children }: InlineProps) {
  const style: CSSProperties = {}
  if (gap) (style as Record<string, string>)['--sbp-inline-gap'] = gap
  if (justify) style.justifyContent = justify === 'start' ? 'flex-start' : justify === 'end' ? 'flex-end' : 'center'
  return (
    <div style={style} className={cx('sbp-inline', nowrap && 'sbp-inline--nowrap', className)}>
      {children}
    </div>
  )
}

interface SplitProps {
  readonly reverse?: boolean
  readonly className?: string
  readonly children: ReactNode
}

export function Split({ reverse, className, children }: SplitProps) {
  return <div className={cx('sbp-split', reverse && 'sbp-split--reverse', className)}>{children}</div>
}

interface GridProps {
  readonly columns?: 1 | 2 | 3 | 4
  readonly as?: 'div' | 'ul'
  readonly className?: string
  readonly children: ReactNode
}

export function Grid({ columns = 3, as = 'div', className, children }: GridProps) {
  const Tag = as as ElementType
  return (
    <Tag style={{ '--sbp-cols': columns } as CSSProperties} className={cx('sbp-grid', className)}>
      {children}
    </Tag>
  )
}

export function Divider({ className }: { readonly className?: string }) {
  return <hr className={cx('sbp-divider', className)} />
}

interface SurfaceProps {
  readonly as?: 'div' | 'article' | 'li'
  readonly interactive?: boolean
  readonly padded?: boolean
  readonly className?: string
  readonly children: ReactNode
}

/** Base panel: background + border + radius + shadow. `padded` adds card padding. */
export function Surface({ as = 'div', interactive, padded = true, className, children }: SurfaceProps) {
  const Tag = as as ElementType
  return (
    <Tag
      className={cx(
        'sbp-surface',
        padded && 'sbp-card',
        interactive && 'sbp-card--interactive',
        className,
      )}
    >
      {children}
    </Tag>
  )
}
